import React, { PropsWithChildren } from 'react';
import { BusEvent } from 'app/common/eventbus/Events';
import { MapTrafficVolume } from 'app/model/CORE';
import AppContext from 'app/common/context/AppContext';
import { Sol49MapTrafficVolumeListRequest } from 'app/model/SOL49';
import Utils from 'app/common/util/Utils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { RootState } from 'app/redux/store';
import { setLoadingData, setTVCache } from 'app/redux/features/map/fullscreen-slice';
import { connect, ConnectedProps } from 'react-redux';

export interface MapTrafficVolumeCacheType {
    [key: string]: MapTrafficVolume;
}

export const MapTrafficVolumeCache = React.createContext<MapTrafficVolumeCacheType>({}); //initializes the cache

const mapStateToProps = (state: RootState, ownProps) => {
    return {
        // loading: state.mapFullscreenData.loadingData
    };
};
const mapDispatchToProps = {
    setLoadingData,
    setTVCache,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface MapTrafficVolumeCacheUtilsProps extends PropsFromRedux {}

interface MapTrafficVolumeCacheUtilsState {
    value: MapTrafficVolumeCacheType;
}

/**
 * Component that handles the load events and can provide the cache in two ways:
 *  - Using React context (Consumer)
 *  - Listen to MapTrafficVolumeCacheUpdated events
 *
 *  TODO: refactor to relly fully on redux (events not needed anymore)
 */
class MapTrafficVolumeCacheComponent extends React.PureComponent<
    MapTrafficVolumeCacheUtilsProps & PropsWithChildren,
    MapTrafficVolumeCacheUtilsState
> {
    private processing: boolean;
    // public static cache: MapTrafficVolumeCacheType = {};

    constructor(props) {
        super(props);
        this.state = {
            value: {},
        };
    }

    componentDidMount(): void {
        this.initBus();
    }

    render() {
        return (
            <MapTrafficVolumeCache.Provider value={this.state.value}>
                {this.props.children}
            </MapTrafficVolumeCache.Provider>
        );
    }

    initBus() {
        AppContext.eventBus.register(LoadMapTrafficVolume.NAME, (event: LoadMapTrafficVolume) => {
            this.retrieveUnknownTvs(event);
        });
    }

    retrieveUnknownTvs(event: LoadMapTrafficVolume) {
        if (this.processing) {
            setTimeout(() => this.retrieveUnknownTvs(event), 500);
            return;
        }
        this.processing = true;
        //discard existing
        const tvIdsToLoad = event.tvIds.filter(tvId => Utils.isNotExists(this.state[tvId]));
        if (tvIdsToLoad.length > 0) {
            this.loadTvsIntoCache(tvIdsToLoad).finally(() => {
                this.processing = false;
            });
        }
    }

    async loadTvsIntoCache(tvIds: Array<string>) {
        const request: Sol49MapTrafficVolumeListRequest = {
            tvIdList: tvIds,
            dataset: AppContext.getDataset(),
            ignoreUnknownTvIds: true,
        };

        this.props.setLoadingData(1);

        const result = await ServiceFactory.sol49Service().getMapTrafficVolumeList(request);
        if (!result.error) {
            const parsedReply = result.data.reduce(
                (result: { [key: string]: MapTrafficVolume }, obj: MapTrafficVolume) => {
                    result[obj.trafficVolume.id] = obj;
                    return result;
                },
                {},
            );
            const updateToCache = {};
            for (const tvId of tvIds) {
                if (parsedReply[tvId]) {
                    updateToCache[tvId] = parsedReply[tvId];
                } else {
                    //TODO understand the source of invalid tv ids
                    console.warn('Invalid tv id', tvId);
                    updateToCache[tvId] = null; //INVALID, otherwise will keep trying to load it
                }
            }
            this.setState(
                prevState => {
                    const newCache = Object.assign({}, prevState.value, updateToCache);
                    MapTrafficVolumeCacheUpdated.fire(newCache, prevState.value);
                    return {
                        value: newCache,
                    };
                },
                () => {
                    console.debug('cache updated', this.state.value);
                    // MapTrafficVolumeCacheComponent.cache = this.state.value;//update static field
                    this.props.setTVCache(this.state.value);
                    this.props.setLoadingData(-1);
                },
            );
        }
    }
}

export class LoadMapTrafficVolume extends BusEvent {
    static NAME = 'LoadMapTrafficVolume';
    tvIds: Array<string>;

    constructor(values: Array<string>) {
        super(LoadMapTrafficVolume.NAME);
        this.tvIds = values;
    }

    static fire(tvIds: Array<string>) {
        AppContext.eventBus.publish(new LoadMapTrafficVolume(tvIds));
    }
}

export class MapTrafficVolumeCacheUpdated extends BusEvent {
    static NAME = 'MapTrafficVolumeCacheUpdated';
    cache: MapTrafficVolumeCacheType;
    previousCache: MapTrafficVolumeCacheType;

    constructor(cache: MapTrafficVolumeCacheType, previousCache: MapTrafficVolumeCacheType) {
        super(MapTrafficVolumeCacheUpdated.NAME);
        this.cache = cache;
        this.previousCache = previousCache;
    }

    static fire(cache: MapTrafficVolumeCacheType, previousCache: MapTrafficVolumeCacheType) {
        AppContext.eventBus.publish(new MapTrafficVolumeCacheUpdated(cache, previousCache));
    }
}

export default connector(MapTrafficVolumeCacheComponent);
